import type { CSSProperties } from 'react'
import React, { memo, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { EColor } from '@frontend/pole-ui/lib/common/constants'
import pick from 'lodash/pick'
import { useApplyFinancingOffer, useApplyLostFinancingOffer } from '@/components/composed/financing/hooks'
import CustomizedLeasingForm from '@/components/composed/forms/CustomizedForms/CustomizedLeasingForm'
import type { ICustomizedLeasingFormProps } from '@/components/composed/forms/CustomizedForms/CustomizedLeasingForm/interfaces'
import { useAuthenticateUser } from '@/components/ui/AuthButton/hooks'
import { isUserAgrarian } from '@/logic/auth/reducer'
import type { IApplyingFinancingOfferRequestBody, IFinancingOfferFormValues } from '@/logic/financing/interfaces'
import { getProfile } from '@/logic/profile/reducer'
import type { RootState } from '@/redux/interfaces'
import { AuthCookiesService } from '@/services'
import { getApplyingFinancingOfferFormInitialValues } from '@/utils/getApplyingFinancingOfferFormInitialValues'
import { getTokenInfo } from '@/utils/helpers'
import { isApplyingOfferRequestBody } from '@/utils/hooks/applyingFinancingOffer/typeguard'
import { MiniConsultingForm } from '../LandingApplyingLeasingOfferForm/components/MiniConsultingForm'
import { LEASING_PRODUCT_IDS, LESSORS_BANK_NAME } from './constants'
import { messages } from './messages'
import type { ILandingLeasingOfferProps } from './types'
import './styles.scss'

const mapState = (state: RootState) => ({
  isAgrarian: isUserAgrarian()(state),
  profile: getProfile()(state),
})

export const LandingLeasingOffer: React.FC<ILandingLeasingOfferProps> = memo(props => {
  const { formId, offerType, productId } = props

  const { profile, isAgrarian } = useSelector(mapState)
  const { isAuthenticated, openAuthModal } = useAuthenticateUser()
  const shouldDisableButton = isAuthenticated && !isAgrarian
  const { applyFinancingOffer } = useApplyFinancingOffer()
  const { applyLostFinancingOffer } = useApplyLostFinancingOffer()

  const initialValues = useMemo(() => {
    const lessorBank = LESSORS_BANK_NAME[offerType]

    return getApplyingFinancingOfferFormInitialValues({
      offerType,
      profile,
      productId,
      bankTitle: lessorBank?.name,
      iconUrl: lessorBank?.url,
      isLeasing: true,
    })
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [requestBody, setRequestBody] = useState<IApplyingFinancingOfferRequestBody>()
  const [isRequestReady, setIsRequestReady] = useState(false)

  const prepareRequestBody = (values: IFinancingOfferFormValues) => {
    const isMultiRequest = values.lessor === 'ALL'
    const formData = pick(values, ['phone', 'email', 'comment', 'agreement', 'loanAmount', 'regionCode'])
    const locationData = pick(values.location, ['city', 'fiasCode'])
    const productIds = isMultiRequest ? LEASING_PRODUCT_IDS : [productId]

    return {
      ...formData,
      ...locationData,
      regionCode: isMultiRequest ? null : formData.regionCode,
      productIds,
    } as IApplyingFinancingOfferRequestBody
  }

  const sendRequest = async (body: IApplyingFinancingOfferRequestBody, isLost = false) => {
    if (!body || !isApplyingOfferRequestBody(body)) return

    const tokenPayload = getTokenInfo(AuthCookiesService.getJwt())?.payload
    const organizationData = pick(tokenPayload, ['fullName', 'organizationName', 'organizationInn'])

    const options = {
      body: {
        ...body,
        ...organizationData,
        productId,
      } as IApplyingFinancingOfferRequestBody,
      isLeasing: true,
      withSuccessModal: true,
    }

    if (isLost) {
      await applyLostFinancingOffer(options)
    } else {
      await applyFinancingOffer(options)
    }
  }

  useEffect(() => {
    if (isAuthenticated && isRequestReady && requestBody) sendRequest(requestBody)
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated])

  const authAndSendRequest = (values: IFinancingOfferFormValues) => {
    setRequestBody(prepareRequestBody(values))
    sendRequest({ ...prepareRequestBody(values) }, true)
    setIsRequestReady(true)

    openAuthModal({
      initialValues: { phone: values.phone, email: values.email },
      shouldSkipSelectRoleStep: true,
      shouldSendCodeImmediately: true,
      onCloseDialog: () => setIsRequestReady(false),
    })
  }

  const handleSubmit: ICustomizedLeasingFormProps['onSubmit'] = async values => {
    if (isAuthenticated) {
      await sendRequest(prepareRequestBody(values))
    } else {
      await authAndSendRequest(values)
    }
  }

  return (
    <section id={formId} className="landingLeasingOffer" style={{ '--active-dot-style': EColor.WHITE } as CSSProperties}>
      <div className="page-wrapper page-wrapper_no-mobile page-wrapper_no-tablet landingLeasingOffer__wrapper">
        <div className="landingLeasingOfferForm">
          <div className="landingLeasingOfferForm__header">
            <h3 className="landingLeasingOfferForm__title">{messages.title}</h3>
            <div className="landingLeasingOfferForm__subtitle">{messages.subtitle}</div>
          </div>
          <CustomizedLeasingForm
            initialValues={initialValues}
            onSubmit={handleSubmit}
            tooltipText={messages.forAgrariansOnly}
            shouldDisableButton={shouldDisableButton}
            regionSelectErrorMessage={props.regionSelectErrorMessage}
          />
        </div>

        <div className="landingLeasingOfferConsult">
          <MiniConsultingForm productId={productId} />
        </div>
      </div>
    </section>
  )
})
